import React from "react"
import ProgressBar from "./ProgressBar"
import { fmt, fmtWhole, fmtHours } from "../helpers/format"

const Card = ({ label, value, sub, tone, subTone, progress }) => (
  <div className="flex flex-col gap-1.5 px-4 py-3 bg-white border border-slate-200 rounded-xl">
    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
    <div className={`text-[18px] font-semibold tabular-nums ${tone || "text-slate-900"}`}>{value}</div>
    {progress != null && <ProgressBar value={progress} tone="brand" height={4} />}
    {sub && <div className={`text-[11.5px] mt-auto ${subTone || "text-slate-500"}`}>{sub}</div>}
  </div>
)

const hoursCard = totals => {
  if (!totals) return null
  const sold = Number(totals.sold) || 0
  const done = Number(totals.done) || 0
  const sub  = totals.done_amount
    ? I18n.t("projects.kpis.hours_valued", { amount: fmt(totals.done_amount) })
    : sold > 0
      ? I18n.t("projects.kpis.hours_done_of_sold", { done: fmtHours(done), sold: fmtHours(sold) })
      : I18n.t("projects.kpis.hours_none")
  return {
    value:    sold > 0 ? `${fmtHours(done)} / ${fmtHours(sold)}` : fmtHours(done),
    progress: sold > 0 ? Math.min(100, (done / sold) * 100) : null,
    tone:     sold > 0 && done > sold ? "text-rose-600" : "text-slate-900",
    sub,
  }
}

const KpiStrip = ({ kpis, hours }) => {
  const overBudget    = kpis.consumption_pct > 100
  const hasAvenants   = (kpis.signed_avenants?.cents || 0) !== 0
  const pendingCents  = kpis.pending_avenants?.cents || 0
  const hoursKpi      = hoursCard(hours?.totals)
  return (
    <div className="px-7 pt-[16px] pb-[16px]">
      <div className={`grid gap-3 grid-cols-2 md:grid-cols-3 ${hoursKpi ? "xl:grid-cols-7" : "xl:grid-cols-6"}`}>
        <Card
          label={I18n.t("projects.completion")}
          value={`${Math.round(kpis.completion || 0)} %`}
          progress={kpis.completion || 0}
          sub={I18n.t("projects.kpis.completion_hint")}
        />
        <Card
          label={I18n.t("projects.finance.initial_budget")}
          value={fmtWhole(kpis.initial_budget)}
          sub={I18n.t("projects.kpis.initial_budget_hint")}
        />
        <Card
          label={I18n.t("projects.finance.revised_contract")}
          value={fmtWhole(kpis.revised_contract)}
          sub={pendingCents !== 0
            ? I18n.t("projects.kpis.pending_avenants", { amount: fmtWhole(kpis.pending_avenants) })
            : hasAvenants
              ? I18n.t("projects.kpis.signed_avenants_included", { amount: fmtWhole(kpis.signed_avenants) })
              : I18n.t("projects.avenants.summary.revised_hint")}
          subTone={pendingCents !== 0 ? "text-amber-600" : null}
          tone={hasAvenants ? "text-blue-700" : "text-slate-900"}
        />
        <Card
          label={I18n.t("projects.finance.committed_cost")}
          value={fmtWhole(kpis.committed_cost)}
          sub={I18n.t("projects.kpis.committed_hint")}
        />
        <Card
          label={I18n.t("projects.finance.actual_cost")}
          value={fmtWhole(kpis.actual_cost)}
          tone={overBudget ? "text-rose-600" : "text-slate-900"}
          progress={Math.min(100, kpis.consumption_pct || 0)}
          sub={I18n.t("projects.kpis.consumption", { pct: Math.round(kpis.consumption_pct || 0) })}
        />
        <Card
          label={I18n.t("projects.kpis.invoiced")}
          value={fmtWhole(kpis.invoiced_amount)}
          sub={I18n.t("projects.kpis.invoiced_hint")}
        />
        {hoursKpi && (
          <Card
            label={I18n.t("projects.kpis.hours")}
            value={hoursKpi.value}
            tone={hoursKpi.tone}
            progress={hoursKpi.progress}
            sub={hoursKpi.sub}
          />
        )}
      </div>
    </div>
  )
}

export default KpiStrip
